/**
 * Authentication Service
 *
 * Supports both AWS Cognito (production) and legacy Django JWT (fallback).
 * Uses AWS Amplify for Cognito integration with Google SSO support.
 */
import axios from 'axios';
import { useAuthStore } from '../stores/authStore';

// Import Amplify config (initializes Cognito)
import '../config/amplify';

// Check if Cognito is configured
const COGNITO_ENABLED = !!(
  process.env.REACT_APP_COGNITO_USER_POOL_ID &&
  process.env.REACT_APP_COGNITO_CLIENT_ID
);

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8080';

/**
 * Create axios instance with interceptors for auth
 */
const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor - add auth token
api.interceptors.request.use(
  async (config) => {
    let token = null;

    if (COGNITO_ENABLED) {
      // Get token from Cognito
      // Use idToken instead of accessToken because it contains custom claims
      // (custom:tenantId, custom:role, custom:status)
      try {
        const { fetchAuthSession } = await import('aws-amplify/auth');
        const session = await fetchAuthSession();
        token = session.tokens?.idToken?.toString();
      } catch (e) {
        // Not authenticated
      }
    } else {
      // Legacy: Get token from store
      const { accessToken } = useAuthStore.getState();
      token = accessToken;
    }

    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Response interceptor - handle auth errors
// Track if we're already logging out to prevent infinite loops
let isLoggingOut = false;

api.interceptors.response.use(
  (response) => response,
  async (error) => {
    // Skip auth error handling if we're already logging out or if this is a logout request
    const isLogoutRequest = error.config?.url?.includes('/logout');

    if (error.response?.status === 401 && !isLoggingOut && !isLogoutRequest) {
      isLoggingOut = true;

      // Token expired or invalid
      if (COGNITO_ENABLED) {
        // Cognito handles token refresh automatically
        // If we still get 401, the session is truly invalid
        // Clear state directly without calling API logout
        try {
          const { signOut } = await import('aws-amplify/auth');
          await signOut();
        } catch {
          // Ignore signOut errors
        }
        // Clear store state directly (not calling logout() to avoid API call)
        useAuthStore.setState({
          user: null,
          accessToken: null,
          refreshToken: null,
          isAuthenticated: false,
          error: null,
        });
      } else {
        // Legacy token refresh logic
        const originalRequest = error.config;
        if (!originalRequest._retry) {
          originalRequest._retry = true;
          const { refreshToken, setTokens } = useAuthStore.getState();

          if (refreshToken) {
            try {
              const response = await axios.post(`${API_BASE_URL}/api/auth/refresh/`, {
                refresh: refreshToken,
              });
              const { access, refresh } = response.data;
              setTokens(access, refresh || refreshToken);
              originalRequest.headers.Authorization = `Bearer ${access}`;
              isLoggingOut = false;
              return api(originalRequest);
            } catch {
              // Clear store state directly without calling API
              useAuthStore.setState({
                user: null,
                accessToken: null,
                refreshToken: null,
                isAuthenticated: false,
                error: null,
              });
            }
          } else {
            // Clear store state directly without calling API
            useAuthStore.setState({
              user: null,
              accessToken: null,
              refreshToken: null,
              isAuthenticated: false,
              error: null,
            });
          }
        }
      }

      isLoggingOut = false;
    }
    return Promise.reject(error);
  }
);

/**
 * Cognito Authentication Service
 */
const cognitoAuthService = {
  /**
   * Register a new user with Cognito + backend
   */
  register: async (data) => {
    const { signUp } = await import('aws-amplify/auth');

    // Sign up with Cognito (only standard attributes)
    // Company is stored in backend only to avoid Cognito attribute issues
    const result = await signUp({
      username: data.email,
      password: data.password,
      options: {
        userAttributes: {
          email: data.email,
          given_name: data.firstName,
          family_name: data.lastName,
        }
      }
    });

    // Register with backend (creates pending registration)
    await api.post('/api/tenants/register/', {
      cognito_user_id: result.userId,
      email: data.email,
      first_name: data.firstName,
      last_name: data.lastName,
      company: data.company,
      reason: data.reason || '',
    });

    return result;
  },

  /**
   * Confirm signup with verification code
   */
  confirmSignUp: async (email, code) => {
    const { confirmSignUp } = await import('aws-amplify/auth');
    return confirmSignUp({ username: email, confirmationCode: code });
  },

  /**
   * Login with email/password
   */
  login: async (email, password) => {
    const { signIn, signOut, fetchAuthSession } = await import('aws-amplify/auth');

    // First, sign out any existing session to avoid conflicts
    try {
      await signOut();
    } catch {
      // Ignore errors - might not be signed in
    }

    // Sign in with Cognito
    const signInResult = await signIn({ username: email, password });

    // Check if sign in is complete
    if (signInResult.isSignedIn !== true) {
      // Handle additional steps if needed (MFA, new password required, etc.)
      if (signInResult.nextStep?.signInStep === 'CONFIRM_SIGN_UP') {
        throw new Error('Please verify your email first');
      }
      throw new Error('Sign in incomplete');
    }

    // Get session and check approval status
    const session = await fetchAuthSession();
    const payload = session.tokens?.idToken?.payload;

    if (payload?.['custom:status'] === 'pending') {
      await signOut();
      throw new Error('PENDING_APPROVAL');
    }

    return signInResult;
  },

  /**
   * Login with Google SSO
   */
  loginWithGoogle: async () => {
    const { signInWithRedirect } = await import('aws-amplify/auth');
    return signInWithRedirect({ provider: 'Google' });
  },

  /**
   * Logout
   */
  logout: async () => {
    const { signOut } = await import('aws-amplify/auth');
    return signOut();
  },

  /**
   * Get current user from Cognito
   */
  getCurrentUser: async () => {
    try {
      const { getCurrentUser, fetchAuthSession } = await import('aws-amplify/auth');
      const user = await getCurrentUser();
      const session = await fetchAuthSession();
      const payload = session.tokens?.idToken?.payload;

      return {
        id: user.userId,
        email: payload?.email,
        firstName: payload?.given_name,
        lastName: payload?.family_name,
        tenantId: payload?.['custom:tenantId'],
        role: payload?.['custom:role'] || 'member',
        status: payload?.['custom:status'] || 'pending',
      };
    } catch {
      return null;
    }
  },

  /**
   * Get JWT token for API calls
   * Uses idToken instead of accessToken to include custom claims
   */
  getToken: async () => {
    const { fetchAuthSession } = await import('aws-amplify/auth');
    const session = await fetchAuthSession();
    return session.tokens?.idToken?.toString();
  },

  /**
   * Reset password - send code
   */
  forgotPassword: async (email) => {
    const { resetPassword } = await import('aws-amplify/auth');
    return resetPassword({ username: email });
  },

  /**
   * Reset password - confirm with code
   */
  confirmResetPassword: async (email, code, newPassword) => {
    const { confirmResetPassword } = await import('aws-amplify/auth');
    return confirmResetPassword({
      username: email,
      confirmationCode: code,
      newPassword,
    });
  },
};

/**
 * Legacy Django JWT Authentication Service
 */
const legacyAuthService = {
  register: async (data) => {
    const response = await api.post('/api/auth/register/', {
      email: data.email,
      password: data.password,
      password_confirm: data.passwordConfirm,
      first_name: data.firstName,
      last_name: data.lastName,
      company: data.company,
      reason: data.reason,
    });
    return response.data;
  },

  login: async (email, password) => {
    const response = await api.post('/api/auth/login/', { email, password });
    return response.data;
  },

  logout: async (refreshToken) => {
    try {
      await api.post('/api/auth/logout/', { refresh: refreshToken });
    } catch {
      // Silently ignore errors on logout
    }
  },

  getCurrentUser: async () => {
    const response = await api.get('/api/auth/me/');
    return response.data;
  },

  getToken: async () => {
    const { accessToken } = useAuthStore.getState();
    return accessToken;
  },

  // Legacy doesn't support these
  loginWithGoogle: async () => {
    throw new Error('Google SSO requires Cognito configuration');
  },
  confirmSignUp: async () => null,
  forgotPassword: async () => null,
  confirmResetPassword: async () => null,
};

/**
 * Main Auth Service - uses Cognito if configured, otherwise legacy
 */
const authService = COGNITO_ENABLED ? cognitoAuthService : legacyAuthService;

/**
 * Admin Service - User & Registration Management
 */
const adminService = {
  /**
   * Get all pending registrations
   */
  getPendingRegistrations: async () => {
    const response = await api.get('/api/tenants/admin/pending/');
    return response.data;
  },

  /**
   * Approve a registration
   */
  approveRegistration: async (id, data = {}) => {
    const response = await api.post(`/api/tenants/admin/approve/${id}/`, data);
    return response.data;
  },

  /**
   * Reject a registration
   */
  rejectRegistration: async (id, reason = '') => {
    const response = await api.post(`/api/tenants/admin/reject/${id}/`, {
      rejection_reason: reason,
    });
    return response.data;
  },

  /**
   * Get all tenants (super admin only)
   */
  getTenants: async () => {
    const response = await api.get('/api/tenants/admin/tenants/');
    return response.data;
  },

  /**
   * Get registrations - uses Cognito/tenants endpoint when Cognito is enabled
   */
  getRegistrations: async (status = null) => {
    if (COGNITO_ENABLED) {
      // Use tenants endpoint for Cognito-based registrations
      const params = status ? { status } : {};
      const response = await api.get('/api/tenants/admin/registrations/', { params });
      return response.data;
    }
    // Legacy: Django-based registrations
    const params = status ? { status } : {};
    const response = await api.get('/api/auth/admin/registrations/', { params });
    return response.data;
  },

  // Legacy admin endpoints (for backwards compatibility)
  getStats: async () => {
    const response = await api.get('/api/auth/admin/stats/');
    return response.data;
  },

  /**
   * Get all users - uses Cognito endpoint when Cognito is enabled
   */
  getUsers: async (filters = {}) => {
    if (COGNITO_ENABLED) {
      const response = await api.get('/api/tenants/admin/users/', { params: filters });
      return response.data;
    }
    // Legacy: Django-based users
    const response = await api.get('/api/auth/admin/users/', { params: filters });
    return response.data;
  },

  /**
   * Update a user - uses Cognito endpoint when Cognito is enabled
   */
  updateUser: async (userId, data) => {
    if (COGNITO_ENABLED) {
      const response = await api.post(`/api/tenants/admin/users/${userId}/`, data);
      return response.data;
    }
    // Legacy: Django-based users
    const response = await api.patch(`/api/auth/admin/users/${userId}/`, data);
    return response.data;
  },

  /**
   * Clean up stale registration entries for the current admin
   */
  cleanupRegistrations: async () => {
    const response = await api.delete('/api/tenants/admin/registrations/cleanup/');
    return response.data;
  },

  /**
   * Permanently delete a user from Cognito and database.
   * User must be disabled first.
   */
  deleteUser: async (userId) => {
    const response = await api.delete(`/api/tenants/admin/users/${userId}/delete/`);
    return response.data;
  },
};

/**
 * Tenant Service - Current tenant management
 */
const tenantService = {
  /**
   * Get current tenant info
   */
  getCurrentTenant: async () => {
    const response = await api.get('/api/tenants/current/');
    return response.data;
  },

  /**
   * Get tenant members
   */
  getMembers: async () => {
    const response = await api.get('/api/tenants/members/');
    return response.data;
  },

  /**
   * Invite a new member
   */
  inviteMember: async (email, role = 'member') => {
    const response = await api.post('/api/tenants/invite/', { email, role });
    return response.data;
  },
};

/**
 * Project Service - User-managed projects (tenants)
 */
const projectService = {
  /**
   * Get all projects the current user is a member of
   */
  getProjects: async () => {
    const response = await api.get('/api/tenants/projects/');
    return response.data;
  },

  /**
   * Create a new project
   */
  createProject: async (name) => {
    const response = await api.post('/api/tenants/projects/', { name });
    return response.data;
  },

  /**
   * Get project details including members
   */
  getProject: async (projectId) => {
    const response = await api.get(`/api/tenants/projects/${projectId}/`);
    return response.data;
  },

  /**
   * Switch to a different project (updates Cognito tenantId)
   * Returns requires_refresh: true - caller must refresh token
   */
  switchProject: async (projectId) => {
    const response = await api.post(`/api/tenants/projects/${projectId}/switch/`);
    return response.data;
  },

  /**
   * Add an existing user to the project
   */
  addMember: async (projectId, email, role = 'member') => {
    const response = await api.post(`/api/tenants/projects/${projectId}/members/`, {
      email,
      role,
    });
    return response.data;
  },

  /**
   * Search users by email for autocomplete
   * @param {string} query - Email prefix to search for (min 2 chars)
   * @param {string} projectId - Project ID to exclude existing members
   * @returns {Array<{email: string, name: string}>} Matching users
   */
  searchUsers: async (query, projectId) => {
    const response = await api.get('/api/tenants/users/search/', {
      params: { q: query, project_id: projectId },
    });
    return response.data.users;
  },

  /**
   * Remove a member from the project
   */
  removeMember: async (projectId, memberId) => {
    const response = await api.delete(
      `/api/tenants/projects/${projectId}/members/${memberId}/`
    );
    return response.data;
  },

  /**
   * Update member permissions (owner only)
   * @param {string} projectId - Project UUID
   * @param {string} memberId - Member UUID
   * @param {boolean} canLaunchSimulations - Whether member can launch simulations
   */
  updateMemberPermissions: async (projectId, memberId, canLaunchSimulations) => {
    const response = await api.patch(
      `/api/tenants/projects/${projectId}/members/${memberId}/permissions/`,
      { can_launch_simulations: canLaunchSimulations }
    );
    return response.data;
  },

  /**
   * Request simulation access for a project (owner only)
   * @param {string} projectId - Project UUID
   * @param {string} reason - Reason for requesting access
   */
  requestSimulationAccess: async (projectId, reason = '') => {
    const response = await api.post(
      `/api/tenants/projects/${projectId}/request-simulation/`,
      { reason }
    );
    return response.data;
  },

  /**
   * Cancel a pending simulation access request (owner only)
   * @param {string} projectId - Project UUID
   */
  cancelSimulationRequest: async (projectId) => {
    const response = await api.post(
      `/api/tenants/projects/${projectId}/cancel-simulation-request/`
    );
    return response.data;
  },
};

/**
 * Superadmin Service - Global admin operations
 * Requires custom:role = 'superadmin' in Cognito
 */
const superadminService = {
  /**
   * Get all projects in the system
   */
  getAllProjects: async () => {
    const response = await api.get('/api/tenants/superadmin/projects/');
    return response.data;
  },

  /**
   * Get project details (admin view)
   */
  getProjectDetail: async (projectId) => {
    const response = await api.get(`/api/tenants/superadmin/projects/${projectId}/`);
    return response.data;
  },

  /**
   * Enable or disable simulations for a project
   * @param {string} projectId - Project UUID
   * @param {boolean} enabled - Whether to enable simulations
   */
  toggleProjectSimulation: async (projectId, enabled) => {
    const response = await api.post(
      `/api/tenants/superadmin/projects/${projectId}/simulation/`,
      { enabled }
    );
    return response.data;
  },

  /**
   * Approve a simulation access request
   * @param {string} projectId - Project UUID
   */
  approveSimulationRequest: async (projectId) => {
    const response = await api.post(
      `/api/tenants/superadmin/projects/${projectId}/approve/`
    );
    return response.data;
  },

  /**
   * Reject a simulation access request
   * @param {string} projectId - Project UUID
   * @param {string} reason - Reason for rejection
   */
  rejectSimulationRequest: async (projectId, reason = '') => {
    const response = await api.post(
      `/api/tenants/superadmin/projects/${projectId}/reject/`,
      { reason }
    );
    return response.data;
  },
};

export {
  api,
  authService,
  adminService,
  tenantService,
  projectService,
  superadminService,
  COGNITO_ENABLED,
};

export default authService;
